import { useState, useEffect, useCallback, useRef } from "react";

import { Box } from "ui/box";
import BeeEditor from "containers/admin/clients/touchpoint/components/bee-editor/bee-editor";
import toast from "components/partials/toast/toast";

import { useCurrentClient } from "hooks/api/use-clients";
import { useBuilderContent } from "hooks/api/use-touchpoint-versions";

import type { EmailAttributes } from "hooks/api/use-touchpoint-versions";
import { fetchBeeConfig } from "hooks/api/use-bee-config";

import { addAnnotationIdToElements, sanitizeBuilderJson } from "utilities";

import { TouchpointType } from "types/touchpoint";
import { BeeConfig, BeeConfigExtended } from "types/bee";

import { BLANK_BEE_TEMPLATE } from "utilities/constants";

interface EmailBuilderDesignTabProps {
  onSave: (field: Record<string, any>) => Promise<void>;
  setIsSaved: (isSaved: boolean) => void;
  setIsSaving: (isSaving: boolean) => void;
}

const EmailBuilderDesignTab = ({ onSave, setIsSaved, setIsSaving }: EmailBuilderDesignTabProps) => {
  const [beeConfig, setBeeConfig] = useState<BeeConfig | undefined>(undefined);

  const initialAttributes = useBuilderContent() as EmailAttributes;

  const currentClient = useCurrentClient();
  // we need to use a ref so that bee looks at the latest onSave function, otherwise it will use the old one and touchpointContent will not be updated
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  const handleDesignChange = useCallback(
    async (jsonFile: string) => {
      setIsSaving(true);
      try {
        const annotatedJson = addAnnotationIdToElements(jsonFile);
        const sanitizedJson = sanitizeBuilderJson(annotatedJson);

        const designPayload = {
          data: {
            htmlOutput: "<>",
            jsonOutput: sanitizedJson,
          },
          sourceType: "BEE",
        };

        await onSaveRef.current(designPayload);
        setIsSaved(true);
      } catch (error) {
        console.error("Error saving design", error);
        setIsSaved(false);
      }
      setIsSaving(false);
    },
    [setIsSaved, setIsSaving],
  );

  useEffect(() => {
    async function getBeeConfig() {
      try {
        const beeConfigData: BeeConfigExtended = await fetchBeeConfig(currentClient.id);

        // Remove banner from bee config before setting it
        const { banner, ...beeConfigWithoutBanner } = beeConfigData;
        setBeeConfig(beeConfigWithoutBanner as BeeConfig);
      } catch (error) {
        console.error(error);
        toast.error({
          data: {
            title: "Failed to get theme",
          },
        });
        setBeeConfig({} as BeeConfig);
      }
    }

    initialAttributes.applyClientTheme ? getBeeConfig() : setBeeConfig({} as BeeConfig);

    // eslint-disable-next-line
  }, []);

  return (
    beeConfig && (
      //margin-top must cancel out the padding-top of the Content component
      <Box sx={{ display: "flex", flex: "1", mt: "-1.5rem" }}>
        <BeeEditor
          themeConfig={beeConfig}
          editorType={TouchpointType.EMAIL}
          initialJson={initialAttributes.data?.jsonOutput ?? BLANK_BEE_TEMPLATE}
          onChange={handleDesignChange}
        />
      </Box>
    )
  );
};

export default EmailBuilderDesignTab;
